import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Common timezones
const TIMEZONES = [
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'UTC', label: 'UTC' },
];

export default function ScheduleMeetingDialog({ open, onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(dayjs().add(1, 'hour').startOf('hour'));
  const [timezone_, setTimezone] = useState(dayjs.tz.guess() || 'Europe/Rome');
  const [invitees, setInvitees] = useState([]);
  const [newInviteeEmail, setNewInviteeEmail] = useState('');
  const [newInviteeName, setNewInviteeName] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setIsScheduled(false);
      setScheduledAt(dayjs().add(1, 'hour').startOf('hour'));
      setTimezone(dayjs.tz.guess() || 'Europe/Rome');
      setInvitees([]);
      setNewInviteeEmail('');
      setNewInviteeName('');
      setCustomMessage('');
      setError(null);
    }
  }, [open]);

  const handleAddInvitee = () => {
    if (!newInviteeEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newInviteeEmail.trim())) {
      setError('Invalid email address');
      return;
    }

    // Check for duplicates
    if (invitees.some((i) => i.email.toLowerCase() === newInviteeEmail.trim().toLowerCase())) {
      setError('This email is already in the list');
      return;
    }

    setInvitees([
      ...invitees,
      {
        email: newInviteeEmail.trim(),
        name: newInviteeName.trim() || null,
      },
    ]);
    setNewInviteeEmail('');
    setNewInviteeName('');
    setError(null);
  };

  const handleRemoveInvitee = (index) => {
    setInvitees(invitees.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddInvitee();
    }
  };

  const handleSubmit = async (sendInvites) => {
    if (!title.trim()) {
      setError('Please enter a meeting title');
      return;
    }

    if (isScheduled && scheduledAt.isBefore(dayjs())) {
      setError('Scheduled time must be in the future');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        title: title.trim(),
        scheduledAt: isScheduled ? scheduledAt.utc().toISOString() : null,
        timezone: isScheduled ? timezone_ : null,
        invitees: invitees.length > 0 ? invitees : null,
        customMessage: customMessage.trim() || null,
        sendInvites: sendInvites && invitees.length > 0,
      };

      await onSubmit(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          Create Meeting
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Title */}
          <TextField
            autoFocus
            fullWidth
            label="Meeting Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
          />

          {/* Schedule toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
              />
            }
            label="Schedule for later"
          />

          {/* Date/Time picker */}
          {isScheduled && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DateTimePicker
                label="Date and Time"
                value={scheduledAt}
                onChange={(newValue) => setScheduledAt(newValue)}
                minDateTime={dayjs()}
                sx={{ width: '100%' }}
              />

              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone_}
                  onChange={(e) => setTimezone(e.target.value)}
                  label="Timezone"
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary">
                Detected timezone: {dayjs.tz.guess()}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Invitees section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonAddIcon color="primary" />
              Invite Participants
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                label="Email"
                type="email"
                value={newInviteeEmail}
                onChange={(e) => setNewInviteeEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ flex: 2 }}
              />
              <TextField
                size="small"
                label="Name (optional)"
                value={newInviteeName}
                onChange={(e) => setNewInviteeName(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddInvitee}
                disabled={!newInviteeEmail.trim()}
              >
                Add
              </Button>
            </Box>

            {invitees.length > 0 && (
              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                {invitees.map((invitee, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={invitee.email}
                      secondary={invitee.name}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" onClick={() => handleRemoveInvitee(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            {invitees.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                No invitees added yet. You can still create the meeting and share the link manually.
              </Typography>
            )}
          </Box>

          {/* Custom message */}
          {isScheduled && invitees.length > 0 && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Custom Message (optional)"
              placeholder="Add a personal message to include in the invitation email..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}

          {/* Info about email sending */}
          {isScheduled && invitees.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Invitees will receive an email with the meeting link and a calendar file (.ics) they can add to their calendar.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>

          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => handleSubmit(false)}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Link Only'}
          </Button>

          {isScheduled && invitees.length > 0 && (
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => handleSubmit(true)}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create & Send Invites'}
            </Button>
          )}

          {(!isScheduled || invitees.length === 0) && (
            <Button
              variant="contained"
              onClick={() => handleSubmit(false)}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Meeting'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
